import { RIYAL } from "@/lib/currency";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FEE_TYPES, PAYERS, type FeeConfig, type Payer, type PayerOption } from "./types";

export interface FeeRowProps {
  label: string;
  fee: FeeConfig;
  totalAmount: string;
  /** Functional updater — receives (prev: FeeConfig) => FeeConfig so
   *  consecutive rapid updates (value + payer in the same React batch)
   *  chain correctly instead of spreading a stale `fee` prop (#3107). */
  onChange: (updater: (prev: FeeConfig) => FeeConfig) => void;
  t: (ar: string, en: string) => string;
  /** Scoping hook for e2e — lets a test target one specific fee row's
   *  amount input / payer select without ambiguity across the 4 identical
   *  FeeRow instances rendered on this step. */
  testId?: string;
  /** Payer option labels — sale contracts pass SALE_PAYERS (بائع/مشتري). */
  payers?: readonly PayerOption[];
  /** Disables the payer select when the business rule fixes the payer
   *  (sale-contract سعي is always on the BUYER). Always pass the matching
   *  `payers` set (SALE_PAYERS) alongside this — a locked select rendered
   *  with the default lease labels would show "Tenant" where "Buyer" is
   *  meant. */
  payerLocked?: boolean;
}

export function FeeRow({
  label,
  fee,
  totalAmount,
  onChange,
  t,
  testId,
  payers = PAYERS,
  payerLocked = false,
}: FeeRowProps) {
  const calculatedAmount =
    fee.type === "percentage" && fee.value && totalAmount
      ? (Number(totalAmount) * Number(fee.value)) / 100
      : fee.type === "fixed" && fee.value
      ? Number(fee.value)
      : 0;

  return (
    <div className="space-y-2 rounded-lg border p-3 bg-muted/30" data-testid={testId}>
      <Label className="text-sm font-medium">{label}</Label>
      <div className="grid grid-cols-3 gap-3">
        {/* Fee Type (percentage or fixed) */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">{t("النوع", "Type")}</Label>
          <Select
            value={fee.type}
            onValueChange={(val) =>
              onChange((prev) => ({ ...prev, type: val as FeeConfig["type"], value: "" }))
            }
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FEE_TYPES.map((ft) => (
                <SelectItem key={ft.value} value={ft.value}>
                  {t(ft.label, ft.labelEn)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Fee Value */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">
            {fee.type === "percentage" ? t("النسبة", "Percentage") : t("المبلغ", "Amount")}
          </Label>
          <div className="relative">
            <Input
              type="number"
              value={fee.value}
              onChange={(e) => onChange((prev) => ({ ...prev, value: e.target.value }))}
              placeholder="0"
              className="h-9 pe-10"
            />
            <span className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">
              {fee.type === "percentage" ? "%" : RIYAL}
            </span>
          </div>
        </div>

        {/* Payer */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">{t("يدفعها", "Paid By")}</Label>
          <Select
            value={fee.payer}
            onValueChange={(val) => onChange((prev) => ({ ...prev, payer: val as Payer }))}
            disabled={payerLocked}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {payers.map((payer) => (
                <SelectItem key={payer.value} value={payer.value}>
                  {t(payer.label, payer.labelEn)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Calculated amount display */}
      {fee.type === "percentage" && fee.value && totalAmount && (
        <p className="text-xs text-muted-foreground">
          {t("المبلغ المحسوب:", "Calculated amount:")} {calculatedAmount.toLocaleString()}&nbsp;{RIYAL}
          {" + "}{(calculatedAmount * 0.15).toLocaleString()} {t("ضريبة القيمة المضافة (15%)", "VAT (15%)")}
          {" = "}{(calculatedAmount * 1.15).toLocaleString()}&nbsp;{RIYAL}
        </p>
      )}
      {fee.type === "fixed" && fee.value && (
        <p className="text-xs text-muted-foreground">
          {t("المبلغ:", "Amount:")} {Number(fee.value).toLocaleString()}&nbsp;{RIYAL}
          {" + "}{(Number(fee.value) * 0.15).toLocaleString()} {t("ضريبة القيمة المضافة (15%)", "VAT (15%)")}
          {" = "}{(Number(fee.value) * 1.15).toLocaleString()}&nbsp;{RIYAL}
        </p>
      )}
    </div>
  );
}
